import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getInsightBySlug, getRelatedInsights } from '@/mocks/insights';
import ArticleFooter from './components/ArticleFooter';
import type { InsightBlock } from '@/mocks/insights';

const homeRoutes = [
  { label: 'Work', href: 'how-we-work' },
  { label: 'Capabilities', href: 'capabilities' },
  { label: 'Approach', href: 'approach' },
  { label: 'Insights', href: 'insights' },
  { label: 'About', href: 'about' },
];

function Block({ block }: { block: InsightBlock }) {
  switch (block.type) {
    case 'heading':
      return (
        <h2 className="mt-10 mb-4 text-2xl md:text-[1.7rem] leading-snug text-foreground-950 font-semibold font-heading">
          {block.text}
        </h2>
      );
    case 'quote':
      return (
        <blockquote className="my-8 border-l-2 border-primary-500 pl-6 md:pl-8 py-2">
          <p className="text-xl md:text-2xl leading-relaxed text-primary-800 font-medium">{block.text}</p>
          {block.by && <p className="mt-3 text-[12px] tracking-[0.2em] uppercase text-foreground-500 font-mono">— {block.by}</p>}
        </blockquote>
      );
    case 'list':
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((item) => (
            <li key={item} className="flex gap-3 text-[15px] leading-[1.8] text-foreground-700">
              <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0"></span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <p className="mb-5 text-[16px] md:text-[17px] leading-[1.95] text-foreground-700">{block.text}</p>
      );
  }
}

export default function InsightDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = slug ? getInsightBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!article) {
      const t = window.setTimeout(() => navigate('/insights', { replace: true }), 0);
      return () => window.clearTimeout(t);
    }
  }, [article, navigate]);

  if (!article) return null;

  const related = getRelatedInsights(article.slug, 2);

  return (
    <main className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="w-full border-b border-background-200 bg-background-50/95 backdrop-blur-xl sticky top-0 z-50">
        <div className="w-full px-6 md:px-10 h-16 md:h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <span className="font-deva text-[24px] md:text-[28px] leading-none text-foreground-950 group-hover:text-primary-700 transition-colors">
              प्रभाव
            </span>
            <span className="hidden sm:block text-[10px] tracking-[0.3em] uppercase font-mono text-foreground-500">
              Prabhaw
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 px-2 py-1.5 rounded-full border border-background-200 bg-background-50/60">
            {homeRoutes.map((l) => (
              <Link
                key={l.label}
                to={`/#${l.href}`}
                className="px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition cursor-pointer whitespace-nowrap font-mono text-foreground-600 hover:text-primary-700 hover:bg-primary-100/70"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-[13px] font-medium hover:bg-primary-600 transition cursor-pointer whitespace-nowrap"
          >
            Work With Us
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative w-full bg-background-100/60 py-14 md:py-20">
        <div className="relative px-6 md:px-16 lg:px-28 max-w-5xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-foreground-500 hover:text-primary-700 font-mono transition cursor-pointer whitespace-nowrap mb-8"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            All Insights
          </Link>
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-primary-700 font-mono">
            <span className="w-2 h-2 rounded-full bg-accent-500"></span>
            {article.cat}
          </span>
          <h1 className="mt-5 text-3xl md:text-5xl lg:text-[3.4rem] leading-[1.12] text-foreground-950 font-semibold tracking-tight">
            {article.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] tracking-[0.15em] uppercase text-foreground-500 font-mono">
            <span className="flex items-center gap-2 whitespace-nowrap">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {article.minutes} min read
            </span>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <div className="w-full px-6 md:px-16 lg:px-28 max-w-5xl mx-auto -mt-4">
        <div className="img-zoom relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-background-200">
          <img src={article.img} alt={article.title} className="absolute inset-0 w-full h-full object-cover object-top" />
        </div>
      </div>

      {/* Body */}
      <article className="w-full px-6 md:px-16 lg:px-28 max-w-3xl mx-auto py-12 md:py-16">
        <p className="mb-8 text-lg md:text-xl leading-relaxed text-foreground-600 border-l-2 border-accent-500 pl-5">
          {article.excerpt}
        </p>
        <div className="divide-y divide-background-200 border-y border-background-200 mb-10 py-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] tracking-[0.25em] uppercase text-foreground-400 font-mono">Share</span>
            <button aria-label="Share on LinkedIn" className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-700 hover:border-primary-300 transition cursor-pointer">
              <i className="ri-linkedin-fill w-4 h-4 flex items-center justify-center"></i>
            </button>
            <button aria-label="Share on X" className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-700 hover:border-primary-300 transition cursor-pointer">
              <i className="ri-twitter-x-fill w-4 h-4 flex items-center justify-center"></i>
            </button>
            <button aria-label="Copy link" className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-700 hover:border-primary-300 transition cursor-pointer">
              <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
        {article.body.map((block, i) => (
          <Block key={i} block={block} />
        ))}
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="w-full bg-background-100/60 py-16 md:py-20 border-t border-background-200">
          <div className="px-6 md:px-16 lg:px-28 max-w-5xl mx-auto">
            <div className="flex items-end justify-between gap-6 mb-8">
              <div>
                <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-500 font-mono">Keep reading</span>
                <h2 className="mt-3 text-2xl md:text-3xl text-foreground-950 font-semibold tracking-tight">Related insights</h2>
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 transition cursor-pointer whitespace-nowrap font-mono uppercase tracking-wider"
              >
                View all <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/insights/${r.slug}`}
                  className="group flex flex-col rounded-2xl overflow-hidden border border-background-200 bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="img-zoom relative w-full aspect-[16/9]">
                    <img src={r.img} alt={r.title} className="absolute inset-0 w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <span className="text-[11px] tracking-[0.2em] uppercase text-primary-700 font-mono">{r.cat}</span>
                    <h3 className="mt-2 text-lg leading-snug text-foreground-950 font-semibold group-hover:text-primary-700 transition-colors">
                      {r.title}
                    </h3>
                    <p className="mt-auto pt-4 text-[12px] text-foreground-500 font-mono">{r.meta}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ArticleFooter />
    </main>
  );
}